import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, apiSearch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/** A building as `POST /facilities/search` returns it. */
export interface FacilityMatch {
  id: string;
  facility_name: string;
  facility_type: string | null;
  residence_type_label: string | null;
  street_address: string | null;
  city: string | null;
  state: string | null;
}

/** `GET /facilities/:id`: the building, the caller's clients there, how many others, and its people. */
export interface FacilityPageData {
  facility: {
    id: string;
    facility_name: string;
    facility_type: string | null;
    residence_type_label: string | null;
    street_address: string | null;
    city: string | null;
    state: string | null;
    zip: string | null;
    county: string | null;
    neighbourhood: string | null;
    neighbourhood_source: 'lookup' | 'person' | null;
    phone: string | null;
    contact_email: string | null;
    /** Free text: present only for those who may edit (123). */
    notes?: string | null;
    latitude: number | null;
    longitude: number | null;
    is_active: boolean;
    /** 123: edits made, not counting the building as it first stood. */
    times_edited: number;
    /** An address the lookup has not answered yet: the old pin is gone, the new one is being found. */
    locating: boolean;
  };
  /** Only the clients row security lets the caller see. */
  clients: {
    client_id: string;
    client_ref: string;
    display_name: string;
    legal_status: string | null;
    case_manager: string | null;
    started_on: string | null;
    started_on_unknown: boolean;
    held_at_load: boolean;
  }[];
  /** Living clients placed here whom the caller cannot see: a number, never a name. */
  others: number;
  placed: number;
  contacts: {
    link_id: string;
    name: string | null;
    job_title: string | null;
    role_label: string | null;
    phone: string | null;
    email: string | null;
    is_primary: boolean;
  }[];
  /** Whether this person may edit the building (tiers 1-2 and the two Directors). The route still decides. */
  may_edit: boolean;
  /** Whether the reader may add a person at this building: the placement gate (contacts module). */
  may_add_contacts: boolean;
}

/**
 * Buildings by name, another name or street, over `POST /facilities/search`: shared
 * reference data, so every building is found whoever is placed there, and the
 * answer carries no client at all (name, type and address only).
 */
export function useFacilitySearch(q: string, limit = 30) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['facilities', 'search', q, limit],
    queryFn: () =>
      apiSearch<{ facilities: FacilityMatch[] }>('/facilities/search', { q, limit }, getToken),
    ...queryDefaults,
    enabled: q.length >= 2,
    placeholderData: (previous) => previous,
  });
}

export function useFacilityPage(id: string | undefined) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['facilities', 'page', id],
    queryFn: () => apiFetch<FacilityPageData>(`/facilities/${id}`, getToken),
    ...queryDefaults,
    enabled: !!id,
  });
}

/** The reasons any edit may give (123's `edit_reason`), read rather than written into the form. */
export interface EditReason { code: string; label: string; needs_text: boolean }

export function useEditReasons(enabled = true) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['edit-reasons'],
    queryFn: () => apiFetch<{ reasons: EditReason[] }>('/edit-reasons', getToken),
    ...queryDefaults,
    staleTime: 60 * 60 * 1000,
    enabled,
  });
}

/** What changing the type would do (123): counts only, asked before Save. */
export interface TypeChangePreview {
  old_type: string | null;
  new_type: string;
  changes: boolean;
  standard_before: { visits_per_year: number; interval_days: number; grace_days: number } | null;
  standard_after: { visits_per_year: number; interval_days: number; grace_days: number } | null;
  placed_now: number;
  ever_placed: number;
  placements: number;
  placements_visible: number;
  relabel: { from_code: string; from_label: string; to_code: string; to_label: string; count: number }[];
  stopping: { code: string; label: string; count: number }[];
  no_new_default: boolean;
  planned_return_before: boolean;
  planned_return_after: boolean;
}

export function useTypeChangePreview(id: string, to: string | null) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['facilities', 'type-change', id, to],
    queryFn: () =>
      apiFetch<{ preview: TypeChangePreview }>(`/facilities/${id}/type-change`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ to }),
      }),
    ...queryDefaults,
    enabled: !!to,
  });
}

export interface FacilityEdit {
  facility_name: string;
  facility_type: string;
  street_address: string;
  city: string;
  state: string;
  zip: string;
  county: string;
  neighbourhood: string;
  phone: string;
  contact_email: string;
  notes: string;
  reason: string;
  reason_text: string;
  keep_old_name: boolean;
  distinct_from: string[];
}

/** A building at the address the edit gives, as the 409 names it. */
export interface AddressCandidate {
  id: string;
  facility_name: string;
  residence_type_label: string | null;
  street_address: string | null;
  city: string | null;
  zip: string | null;
  clients_here: number;
}

export function useEditFacility(id: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: FacilityEdit) =>
      apiFetch<{ version: number }>(`/facilities/${id}/edit`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['facilities'] });
      void queryClient.invalidateQueries({ queryKey: ['visits'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export interface FacilityEditRecord {
  version: number;
  reason: string;
  reason_label: string;
  reason_text: string | null;
  old_name_kept: boolean;
  placements_relabelled: number;
  edited_by: string | null;
  edited_at: string;
  /** `withheld`: the notes changed, and what they said is shown only to those who may edit. */
  changes: { field: string; label: string; before: string | null; after: string | null; withheld?: boolean }[];
}

export function useFacilityVersions(id: string, enabled: boolean) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['facilities', 'versions', id],
    queryFn: () => apiFetch<{ edits: FacilityEditRecord[] }>(`/facilities/${id}/versions`, getToken),
    ...queryDefaults,
    enabled,
  });
}
